export interface BetaFeatures {
  algorithmicTimeline?: boolean;
  analysisFetchExternalUrls?: boolean;
  arena?: boolean;
  conversationPrefetch?: boolean;
  // Hidden debug flag controlled by admins; not exposed in the UI toggle list.
  debugLogging?: boolean;
  powerUser?: boolean;
  timelineSelfReplyLinks?: boolean;
  universalChat?: boolean;
  websiteHoverPreview?: boolean;
}

const PUBLIC_BETA_FEATURE_KEYS = [
  'websiteHoverPreview',
  'arena',
  'universalChat',
  'conversationPrefetch',
  'powerUser',
  'algorithmicTimeline',
  'analysisFetchExternalUrls',
  'timelineSelfReplyLinks',
] as const;

const HIDDEN_BETA_FEATURE_KEYS = ['debugLogging'] as const;

export type BetaFeatureKey = (typeof PUBLIC_BETA_FEATURE_KEYS)[number];
type AllBetaFeatureKey = BetaFeatureKey | (typeof HIDDEN_BETA_FEATURE_KEYS)[number];

export interface BetaFeatureInfo {
  description: string;
  name: string;
}

export const DEFAULT_BETA_FEATURES: BetaFeatures = {
  websiteHoverPreview: true,
  arena: false,
  universalChat: false,
  conversationPrefetch: false,
  powerUser: false,
  algorithmicTimeline: false,
  analysisFetchExternalUrls: true,
  timelineSelfReplyLinks: false,
  debugLogging: false,
};

export function normalizeBetaFeatures(raw: unknown): BetaFeatures {
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_BETA_FEATURES };
  }

  const result: BetaFeatures = { ...DEFAULT_BETA_FEATURES };
  const candidate = raw as Partial<Record<AllBetaFeatureKey, unknown>>;

  for (const key of [...PUBLIC_BETA_FEATURE_KEYS, ...HIDDEN_BETA_FEATURE_KEYS]) {
    const value = candidate[key];
    if (typeof value === 'boolean') {
      result[key] = value;
    }
  }

  return result;
}

export const BETA_FEATURE_LABELS: Record<BetaFeatureKey, BetaFeatureInfo> = {
  websiteHoverPreview: {
    name: 'Website Hover Previews',
    description: 'Show preview cards when hovering over links',
  },
  arena: {
    name: 'Arena',
    description: 'Run multi-account compatibility analyses with AI scoring',
  },
  universalChat: {
    name: 'Universal Chat',
    description: 'Persistent AI assistant that follows you across dashboards',
  },
  conversationPrefetch: {
    name: 'Conversation Prefetch',
    description: 'Hover over tweets to warm up conversation data for instant inline expansion',
  },
  powerUser: {
    name: 'Power User Mode',
    description: 'Unlock advanced analysis tools and power utilities normally limited to admins',
  },
  algorithmicTimeline: {
    name: 'Algorithmic Timeline',
    description: 'Enable the For You feed captured by the browser extension alongside the standard timeline.',
  },
  analysisFetchExternalUrls: {
    name: 'Fetch External URLs for analysis',
    description: 'Fetch article text for AI summaries',
  },
  timelineSelfReplyLinks: {
    name: 'Self-reply Link Detection',
    description:
      'Experiment with background Twitter conversation fetches to surface “reply with link” promos in Links.',
  },
};
